/**
 * Read Excel Tool — Factory
 *
 * Creates a RuntimeToolDefinition for reading workbook data.
 */
import { ToolBase, ToolDescription } from "../../../agents/base";
import { getAgentServices } from "../../../services";
import { EXCEL_BACKEND_URL, PYTHON_BACKEND_URL, getResolvedPythonBackendUrl, } from "@carousel-office/shared";
import { ReadExcelParamsSchema, } from "./readExcel.schema";
import { createTelemetry, isDetailedReadMode, mergeErrors, mergeWarnings, normalizeReadRequest, resolveSessionId, summarizeBucket, unwrapRequest, } from "../readHelpers";
const CONTENT_MODE_TO_BACKEND = {
    structure: "structure",
    structureWithFormulaPatterns: "regular",
    structureWithFormulaPatternsAndValues: "regular_and_values",
    detailedValues: "values",
    detailedValuesAndFormulas: "values_and_formulas",
};
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT DESCRIPTION
// ═══════════════════════════════════════════════════════════════════════════
const SYSTEM_PROMPT_DESCRIPTION = `### readExcel
Read workbook data (list sheets, content, formatting, charts, formula trace, table analysis).

**Operations** (combine multiple in one call):
- \`listSheets: [{}]\` — list all sheet names and indexes
- \`readContent: [{sheetName, targetRange?, mode}]\` — read cell values/formulas
  Modes: structure, structureWithFormulaPatterns, structureWithFormulaPatternsAndValues, detailedValues, detailedValuesAndFormulas
- \`readFormatting: [{sheetName, targetRange}]\` — read sharedFormatRanges (grouped fonts/fills/borders/alignment)
- \`listCharts: [{sheetName}]\` — lightweight chart discovery for a sheet
- \`readCharts: [{sheetName, chartId?|chartName?}]\` — read authoritative chart payloads (chartXml/readId/version)
- \`formulaTrace: [{sheetName, cellAddress, direction?}]\` — trace precedents/dependents
- \`tableAnalysis: [{sheetName, targetRange}]\` — analyse data patterns

**Always use \`targetRange\`** (not legacy \`range\` field) with readContent.`;
// ═══════════════════════════════════════════════════════════════════════════
// TOOL FACTORY
// ═══════════════════════════════════════════════════════════════════════════
export function createReadExcelTool(runner, fallbackSessionId) {
    return new ReadExcelTool(runner, fallbackSessionId).toRuntimeToolDefinition();
}
function asRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function getString(value) {
    return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}
function hasKeys(value) {
    return Object.keys(value).length > 0;
}
function normalizeSheetMetadata(rawMetadata, fallbackSheetName) {
    const metadata = asRecord(rawMetadata);
    const sheetName = getString(metadata.sheetName) ||
        getString(metadata.sheet_name) ||
        getString(fallbackSheetName);
    const normalized = {};
    if (sheetName)
        normalized.sheetName = sheetName;
    const visibility = getString(metadata.visibility);
    if (visibility)
        normalized.visibility = visibility;
    const positionValue = typeof metadata.position === "number" && Number.isFinite(metadata.position)
        ? metadata.position
        : undefined;
    if (positionValue !== undefined)
        normalized.position = positionValue;
    const tabColorHex = getString(metadata.tabColorHex) || getString(metadata.tab_color_hex);
    if (tabColorHex)
        normalized.tabColorHex = tabColorHex;
    const tabColorRgb = metadata.tabColorRgb ?? metadata.tab_color_rgb;
    if (Array.isArray(tabColorRgb) && tabColorRgb.length >= 3) {
        const [r, g, b] = tabColorRgb;
        if (typeof r === "number" &&
            Number.isFinite(r) &&
            typeof g === "number" &&
            Number.isFinite(g) &&
            typeof b === "number" &&
            Number.isFinite(b)) {
            normalized.tabColorRgb = [r, g, b];
        }
    }
    if (!hasKeys(normalized) && fallbackSheetName) {
        return { sheetName: fallbackSheetName };
    }
    return hasKeys(normalized) ? normalized : undefined;
}
function applySheetMetadataAliases(base, sheetMetadata) {
    if (!sheetMetadata)
        return base;
    return {
        ...base,
        sheetMetadata,
        // Backward-compatible alias during migration.
        sheetMeta: sheetMetadata,
    };
}
function normalizeGeneralReadRow(rawRow) {
    const row = asRecord(rawRow);
    if (!hasKeys(row))
        return row;
    const rowData = asRecord(row.data);
    const hasWrappedPayload = hasKeys(rowData) &&
        (row.recapInfo !== undefined ||
            row.grouping !== undefined ||
            row.asString !== undefined ||
            row.success !== undefined);
    const base = hasWrappedPayload ? { ...rowData } : { ...row };
    const recap = asRecord(row.recapInfo);
    const sheetName = getString(base.sheetName) ||
        getString(base.sheet_name) ||
        getString(row.sheetName) ||
        getString(row.sheet_name) ||
        getString(recap.sheetName);
    const targetRange = getString(base.targetRange) ||
        getString(base.target_range) ||
        getString(row.targetRange) ||
        getString(row.target_range) ||
        getString(row.read_image_range) ||
        getString(recap.item);
    const merged = {
        ...base,
        ...(sheetName ? { sheetName } : {}),
        ...(targetRange ? { targetRange } : {}),
    };
    if (row.recapInfo !== undefined && merged.recapInfo === undefined) {
        merged.recapInfo = row.recapInfo;
    }
    if (row.grouping !== undefined && merged.grouping === undefined) {
        merged.grouping = row.grouping;
    }
    if (row.asString !== undefined && merged.asString === undefined) {
        merged.asString = row.asString;
    }
    if (row.success !== undefined && merged.success === undefined) {
        merged.success = row.success;
    }
    if (row.error !== undefined && merged.error === undefined) {
        merged.error = row.error;
    }
    // Preserve image payload aliases from backend/OfficeJS rows.
    if (row.read_image_base64 !== undefined)
        merged.read_image_base64 = row.read_image_base64;
    if (row.range_image_base64 !== undefined)
        merged.range_image_base64 = row.range_image_base64;
    if (row.rangeImageBase64 !== undefined)
        merged.rangeImageBase64 = row.rangeImageBase64;
    if (row.rangeImageMimeType !== undefined)
        merged.rangeImageMimeType = row.rangeImageMimeType;
    if (row.chartImageBase64 !== undefined)
        merged.chartImageBase64 = row.chartImageBase64;
    if (row.chartImageMimeType !== undefined)
        merged.chartImageMimeType = row.chartImageMimeType;
    if (row.read_image_range !== undefined)
        merged.read_image_range = row.read_image_range;
    const sheetMetadata = normalizeSheetMetadata(row.sheetMetadata ?? row.sheetMeta ?? base.sheetMetadata ?? base.sheetMeta, sheetName);
    return applySheetMetadataAliases(merged, sheetMetadata);
}
function normalizeListSheetsRows(rawRows) {
    const normalized = [];
    for (const rawRow of rawRows) {
        const row = asRecord(rawRow);
        if (!hasKeys(row))
            continue;
        const metadataListRaw = Array.isArray(row.sheetMetadataList)
            ? row.sheetMetadataList
            : Array.isArray(row.sheetMetaList)
                ? row.sheetMetaList
                : undefined;
        if (metadataListRaw?.length) {
            for (const metadataEntry of metadataListRaw) {
                normalized.push(normalizeGeneralReadRow({ sheetMetadata: metadataEntry }));
            }
            continue;
        }
        const sheetNames = Array.isArray(row.sheets)
            ? row.sheets.filter((value) => typeof value === "string" && value.trim().length > 0)
            : [];
        if (sheetNames.length > 0) {
            const byName = asRecord(row.sheetMetadataByName ?? row.sheetMetaByName);
            for (const sheetName of sheetNames) {
                const metadata = byName[sheetName] || { sheetName };
                normalized.push(normalizeGeneralReadRow({ sheetName, sheetMetadata: metadata }));
            }
            continue;
        }
        normalized.push(normalizeGeneralReadRow(row));
    }
    return normalized;
}
function normalizeReadBucketRows(bucket, rawRows) {
    if (!Array.isArray(rawRows) || rawRows.length === 0)
        return [];
    if (bucket === "listSheets") {
        return normalizeListSheetsRows(rawRows);
    }
    return rawRows.map((row) => normalizeGeneralReadRow(row));
}
let readImageCounter = 0;
function makeReadImageId(seed) {
    readImageCounter += 1;
    const safeSeed = seed.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 48) || "image";
    return `excel_read_${safeSeed}_${Date.now()}_${readImageCounter}`;
}
function collectImageCandidates(rawResult) {
    const result = asRecord(rawResult);
    const candidates = [];
    const seen = new Set();
    const tryPush = (base64, mimeType) => {
        if (typeof base64 !== "string" || base64.trim().length === 0)
            return;
        const trimmed = base64.trim();
        if (seen.has(trimmed))
            return;
        seen.add(trimmed);
        candidates.push({
            base64: trimmed,
            mimeType: typeof mimeType === "string" && mimeType.trim().length > 0 ? mimeType : "image/png",
        });
    };
    tryPush(result.read_image_base64, "image/png");
    tryPush(result.range_image_base64, "image/png");
    tryPush(result.rangeImageBase64, result.rangeImageMimeType);
    tryPush(result.chartImageBase64, result.chartImageMimeType);
    return candidates;
}
function storeReadImages(rawResult, seed) {
    const candidates = collectImageCandidates(rawResult);
    if (candidates.length === 0)
        return [];
    const resolver = getAgentServices().contextResolver;
    if (!resolver?.storeImage)
        return [];
    const imageIds = [];
    for (let i = 0; i < candidates.length; i += 1) {
        const id = makeReadImageId(`${seed}_${i + 1}`);
        resolver.storeImage(id, candidates[i].base64, candidates[i].mimeType, {
            source: "readExcel",
            seed,
        });
        imageIds.push(id);
    }
    return imageIds;
}
function normalizeRangeForInfo(range) {
    if (!range || !String(range).trim())
        return "FULL_SHEET";
    const raw = String(range).trim();
    if (raw.toUpperCase() === "FULL_SHEET")
        return "FULL_SHEET";
    const rangePart = raw.includes("!") ? raw.split("!", 2)[1] : raw;
    const normalized = rangePart.replace(/\$/g, "").replace(/\s+/g, "").toUpperCase();
    return normalized || "FULL_SHEET";
}
function normalizeChartRangeForInfo(chartRef) {
    const raw = String(chartRef || "").trim();
    const normalized = raw ? raw.toLowerCase() : "unknown";
    return `CHART:${normalized}`;
}
function getContentDetailRank(mode) {
    switch (mode) {
        case "structure":
            return 0;
        case "structureWithFormulaPatterns":
            return 1;
        case "structureWithFormulaPatternsAndValues":
            return 2;
        case "detailedValues":
            return 3;
        case "detailedValuesAndFormulas":
            return 4;
        default:
            return 0;
    }
}
function getFormattingDetailRank(includeContent) {
    return includeContent ? 2 : 1;
}
function getChartDetailRank(mode) {
    const normalized = String(mode || "point-level").trim().toLowerCase();
    if (normalized === "point-level")
        return 4;
    if (normalized === "summary-style")
        return 3;
    return 2;
}
function buildContentReadInfo(requestItem, rawResult, imageIds) {
    const result = asRecord(rawResult);
    const recap = asRecord(result.recapInfo);
    const mode = getString(result.mode) ||
        getString(recap.mode) ||
        requestItem?.mode ||
        "structureWithFormulaPatternsAndValues";
    const sheetName = getString(result.sheetName) ||
        getString(result.sheet_name) ||
        getString(recap.sheetName) ||
        requestItem?.sheetName ||
        "Unknown";
    const range = normalizeRangeForInfo(getString(result.targetRange) ||
        getString(result.target_range) ||
        getString(result.read_image_range) ||
        getString(recap.item) ||
        requestItem?.targetRange);
    return {
        track: "content",
        type: "read",
        sheetName,
        rangeA1: range,
        detailLevel: mode,
        detailRank: getContentDetailRank(mode),
        timestamp: Date.now(),
        imageIds: imageIds?.length ? imageIds : undefined,
    };
}
function buildFormattingReadInfo(requestItem, rawResult, imageIds) {
    const result = asRecord(rawResult);
    const recap = asRecord(result.recapInfo);
    const includeContent = (typeof result.includeContent === "boolean" ? result.includeContent : undefined) ??
        (typeof result.include_content === "boolean" ? result.include_content : undefined) ??
        requestItem?.includeContent ??
        true;
    const sheetName = getString(result.sheetName) ||
        getString(result.sheet_name) ||
        getString(recap.sheetName) ||
        requestItem?.sheetName ||
        "Unknown";
    const range = normalizeRangeForInfo(getString(result.targetRange) ||
        getString(result.target_range) ||
        getString(result.read_image_range) ||
        getString(recap.item) ||
        requestItem?.targetRange);
    return {
        track: "formatting",
        type: "read",
        sheetName,
        rangeA1: range,
        detailLevel: includeContent ? "formattingWithContent" : "formattingOnly",
        detailRank: getFormattingDetailRank(includeContent),
        timestamp: Date.now(),
        imageIds: imageIds?.length ? imageIds : undefined,
    };
}
function pickRequestItemForChartResult(requestItems, result, index) {
    if (!requestItems.length)
        return undefined;
    const sheetName = getString(result.sheetName) || getString(result.sheet_name);
    const chartId = getString(result.chartId) || getString(result.chart_id);
    const chartName = getString(result.chartName) || getString(result.chart_name);
    if (!sheetName) {
        return requestItems[Math.min(index, requestItems.length - 1)];
    }
    const exact = requestItems.find((item) => {
        if (item.sheetName !== sheetName)
            return false;
        if (chartId && item.chartId === chartId)
            return true;
        if (chartName && item.chartName === chartName)
            return true;
        if (!chartId && !chartName && !item.chartId && !item.chartName)
            return true;
        return false;
    });
    if (exact)
        return exact;
    const sheetOnly = requestItems.find((item) => item.sheetName === sheetName);
    if (sheetOnly)
        return sheetOnly;
    return requestItems[Math.min(index, requestItems.length - 1)];
}
function buildChartReadInfo(requestItem, rawResult, imageIds) {
    const result = asRecord(rawResult);
    const sheetName = getString(result.sheetName) ||
        getString(result.sheet_name) ||
        requestItem?.sheetName ||
        "Unknown";
    const chartRef = getString(result.chartId) ||
        getString(result.chart_id) ||
        getString(result.chartName) ||
        getString(result.chart_name) ||
        requestItem?.chartId ||
        requestItem?.chartName ||
        "unknown";
    const mode = getString(result.dataMode) ||
        getString(result.data_mode) ||
        requestItem?.dataMode ||
        "point-level";
    return {
        track: "content",
        type: "read",
        sheetName,
        rangeA1: normalizeChartRangeForInfo(chartRef),
        detailLevel: `readCharts:${mode}`,
        detailRank: getChartDetailRank(mode),
        timestamp: Date.now(),
        imageIds: imageIds?.length ? imageIds : undefined,
    };
}
function isRunner(value) {
    return typeof value.read === "function";
}
function parseFileIndex(value) {
    if (value === "CURRENT")
        return "CURRENT";
    if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
        return value;
    }
    return undefined;
}
export class ReadExcelTool extends ToolBase {
    constructor(runnerOrConfig = { mode: "session" }, fallbackSessionId) {
        super();
        this.name = "readExcel";
        this.description = new ToolDescription("readExcel", "Read workbook data (list sheets, content, formatting, charts, formula trace, table analysis).", ReadExcelParamsSchema);
        this.paramsSchema = ReadExcelParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
        this.filepathCache = new Map();
        if (isRunner(runnerOrConfig)) {
            this.runner = runnerOrConfig;
            this.mode = "runner";
            this.fallbackSessionId = fallbackSessionId;
            this.excelBackendUrl = EXCEL_BACKEND_URL.replace(/\/+$/, "");
            this.pythonBackendUrl = getResolvedPythonBackendUrl(PYTHON_BACKEND_URL).replace(/\/+$/, "");
            return;
        }
        const config = runnerOrConfig;
        this.mode = config.mode;
        this.fallbackSessionId = fallbackSessionId;
        this.excelBackendUrl = (config.excelBackendUrl || EXCEL_BACKEND_URL).replace(/\/+$/, "");
        this.pythonBackendUrl = getResolvedPythonBackendUrl(config.pythonBackendUrl || PYTHON_BACKEND_URL).replace(/\/+$/, "");
    }
    setSessionId(sessionId) {
        this.fallbackSessionId = sessionId;
    }
    clearFilepathCache() {
        this.filepathCache.clear();
    }
    async execute(args) {
        if (this.mode === "runner") {
            return this.executeWithRunner(args);
        }
        return this.executeWithBackendAdapter(args);
    }
    hasAnyOperation(request) {
        const requestRecord = asRecord(request);
        const legacyListCharts = requestRecord.listCharts;
        return (!!request.readContent?.length ||
            !!request.readFormatting?.length ||
            !!request.readCharts?.length ||
            !!request.formulaTrace?.length ||
            !!request.tableAnalysis?.length ||
            !!request.listSheets?.length ||
            (Array.isArray(legacyListCharts) && legacyListCharts.length > 0));
    }
    finalizeResult(params) {
        const { startedAt, operationCount, data, warnings, errors, imageIds, excelReadInfos, } = params;
        const finalResult = {
            success: errors.length === 0,
            data,
            results: data,
            _imageIds: imageIds.length ? imageIds : undefined,
            _excelReadInfos: excelReadInfos.length ? excelReadInfos : undefined,
            warnings: warnings.length ? warnings : undefined,
            errors: errors.length ? errors : undefined,
            telemetry: createTelemetry(operationCount, startedAt),
        };
        console.log(`[readExcel] return success=${finalResult.success} warnings=${warnings.length} errors=${errors.length} buckets=` +
            JSON.stringify({
                readContent: summarizeBucket(data.readContent),
                readFormatting: summarizeBucket(data.readFormatting),
                listCharts: summarizeBucket(data.listCharts),
                readCharts: summarizeBucket(data.readCharts),
                formulaTrace: summarizeBucket(data.formulaTrace),
                tableAnalysis: summarizeBucket(data.tableAnalysis),
                listSheets: summarizeBucket(data.listSheets),
            }));
        return finalResult;
    }
    async executeWithRunner(args) {
        if (!this.runner) {
            throw new Error("readExcel runner adapter is not configured.");
        }
        const startedAt = Date.now();
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        let request = normalizeReadRequest(unwrapRequest(args));
        const warnings = [];
        const errors = [];
        const data = {};
        const imageIds = [];
        const excelReadInfos = [];
        let operationCount = 0;
        if (!this.hasAnyOperation(request)) {
            request = {
                ...request,
                listSheets: [{}],
                readContent: [{ sheetName: "", mode: "structure" }],
            };
            warnings.push("No read operation was specified. Applied defaults: listSheets + readContent(mode='structure') on active sheet.");
        }
        console.log(`[readExcel] execute runner=${this.runner.runnerType} session=${sessionId ? "provided" : "none"} ops=` +
            JSON.stringify({
                readContent: request.readContent?.length || 0,
                readFormatting: request.readFormatting?.length || 0,
                readCharts: request.readCharts?.length || 0,
                formulaTrace: request.formulaTrace?.length || 0,
                tableAnalysis: request.tableAnalysis?.length || 0,
                listSheets: request.listSheets?.length || 0,
            }));
        if (request.readContent?.length) {
            console.log(`[readExcel] readContent request items=${request.readContent.length} sample=` +
                JSON.stringify(request.readContent.slice(0, 2)));
            const invalidItems = request.readContent.filter((item) => isDetailedReadMode(item.mode) && !item.targetRange);
            if (invalidItems.length > 0) {
                errors.push("readContent: detailedValues/detailedValuesAndFormulas require targetRange.");
            }
            else {
                operationCount += request.readContent.length;
                const result = await this.runner.read({ readContent: request.readContent }, { sessionId });
                mergeWarnings(warnings, result.warnings, "readContent");
                mergeErrors(errors, result.errors, "readContent");
                if (result.success) {
                    const payload = asRecord(result.data);
                    const rows = normalizeReadBucketRows("readContent", Array.isArray(payload.readContent) ? payload.readContent : []);
                    data.readContent = rows;
                    for (let i = 0; i < rows.length; i += 1) {
                        const requestItem = request.readContent[Math.min(i, request.readContent.length - 1)];
                        const scopedImageIds = storeReadImages(rows[i], `content_${requestItem?.sheetName || "unknown"}_${requestItem?.targetRange || "FULL_SHEET"}`);
                        imageIds.push(...scopedImageIds);
                        excelReadInfos.push(buildContentReadInfo(requestItem, rows[i], scopedImageIds));
                    }
                }
            }
        }
        if (request.readFormatting?.length) {
            operationCount += request.readFormatting.length;
            const result = await this.runner.read({ readFormatting: request.readFormatting }, { sessionId });
            mergeWarnings(warnings, result.warnings, "readFormatting");
            mergeErrors(errors, result.errors, "readFormatting");
            if (result.success) {
                const payload = asRecord(result.data);
                const rows = normalizeReadBucketRows("readFormatting", Array.isArray(payload.readFormatting) ? payload.readFormatting : []);
                data.readFormatting = rows;
                for (let i = 0; i < rows.length; i += 1) {
                    const requestItem = request.readFormatting[Math.min(i, request.readFormatting.length - 1)];
                    const scopedImageIds = storeReadImages(rows[i], `formatting_${requestItem?.sheetName || "unknown"}_${requestItem?.targetRange || "FULL_SHEET"}`);
                    imageIds.push(...scopedImageIds);
                    excelReadInfos.push(buildFormattingReadInfo(requestItem, rows[i], scopedImageIds));
                }
            }
        }
        if (request.readCharts?.length) {
            operationCount += request.readCharts.length;
            const result = await this.runner.read({ readCharts: request.readCharts }, { sessionId });
            mergeWarnings(warnings, result.warnings, "readCharts");
            mergeErrors(errors, result.errors, "readCharts");
            if (result.success) {
                const payload = asRecord(result.data);
                const rows = normalizeReadBucketRows("readCharts", Array.isArray(payload.readCharts) ? payload.readCharts : []);
                data.readCharts = rows;
                for (let i = 0; i < rows.length; i += 1) {
                    const resultRecord = asRecord(rows[i]);
                    const requestItem = pickRequestItemForChartResult(request.readCharts, resultRecord, i);
                    const scopedImageIds = storeReadImages(rows[i], `chart_${requestItem?.sheetName || "unknown"}_${requestItem?.chartId || requestItem?.chartName || "unknown"}`);
                    imageIds.push(...scopedImageIds);
                    excelReadInfos.push(buildChartReadInfo(requestItem, rows[i], scopedImageIds));
                }
            }
        }
        if (request.formulaTrace?.length) {
            const rangeAddress = request.formulaTrace.find((item) => item.cellAddress?.includes(":"));
            if (rangeAddress) {
                errors.push(`formulaTrace: requires a single cell, not range '${rangeAddress.cellAddress}'.`);
            }
            else {
                operationCount += request.formulaTrace.length;
                const result = await this.runner.read({ formulaTrace: request.formulaTrace }, { sessionId });
                mergeWarnings(warnings, result.warnings, "formulaTrace");
                mergeErrors(errors, result.errors, "formulaTrace");
                if (result.success) {
                    const payload = asRecord(result.data);
                    data.formulaTrace = normalizeReadBucketRows("formulaTrace", Array.isArray(payload.formulaTrace) ? payload.formulaTrace : []);
                }
            }
        }
        if (request.tableAnalysis?.length) {
            operationCount += request.tableAnalysis.length;
            const result = await this.runner.read({ tableAnalysis: request.tableAnalysis }, { sessionId });
            mergeWarnings(warnings, result.warnings, "tableAnalysis");
            mergeErrors(errors, result.errors, "tableAnalysis");
            if (result.success) {
                const payload = asRecord(result.data);
                data.tableAnalysis = normalizeReadBucketRows("tableAnalysis", Array.isArray(payload.tableAnalysis) ? payload.tableAnalysis : []);
            }
        }
        if (request.listSheets?.length) {
            operationCount += request.listSheets.length;
            const result = await this.runner.read({ listSheets: request.listSheets }, { sessionId });
            mergeWarnings(warnings, result.warnings, "listSheets");
            mergeErrors(errors, result.errors, "listSheets");
            if (result.success) {
                const payload = asRecord(result.data);
                data.listSheets = normalizeReadBucketRows("listSheets", Array.isArray(payload.listSheets) ? payload.listSheets : []);
            }
        }
        return this.finalizeResult({
            startedAt,
            operationCount,
            data,
            warnings,
            errors,
            imageIds,
            excelReadInfos,
        });
    }
    collectBackendWarnings(warnings, opName, payload) {
        if (!Array.isArray(payload.warnings))
            return;
        const normalized = payload.warnings.filter((value) => typeof value === "string" && value.trim().length > 0);
        mergeWarnings(warnings, normalized, opName);
    }
    attachFileIndex(row, fileIndex) {
        if (fileIndex === undefined)
            return row;
        return { ...row, fileIndex };
    }
    async resolveFilepathForIndex(fileIndex) {
        const cached = this.filepathCache.get(fileIndex);
        if (cached)
            return cached;
        const response = await fetch(`${this.pythonBackendUrl}/rag/files`);
        if (!response.ok) {
            throw new Error(`Failed to resolve file index ${fileIndex}: /rag/files returned ${response.status}`);
        }
        const payload = asRecord(await response.json());
        const files = Array.isArray(payload.files) ? payload.files : [];
        if (fileIndex >= files.length) {
            throw new Error(`File index ${fileIndex} is out of range (available files: ${files.length}).`);
        }
        const file = asRecord(files[fileIndex]);
        const fullPath = getString(file.full_path) || getString(file.filepath);
        if (!fullPath) {
            throw new Error(`File index ${fileIndex} does not have a usable filepath.`);
        }
        this.filepathCache.set(fileIndex, fullPath);
        return fullPath;
    }
    async getApiBaseForItem(fileIndex, sessionId) {
        if (this.mode === "session") {
            if (!sessionId) {
                throw new Error("No active session. The agent must create a session first.");
            }
            return { session_id: sessionId };
        }
        if (fileIndex === undefined) {
            throw new Error("fileIndex is required in filepath mode.");
        }
        if (fileIndex === "CURRENT") {
            if (!sessionId) {
                throw new Error("fileIndex='CURRENT' requires an active session.");
            }
            return { session_id: sessionId };
        }
        return { filepath: await this.resolveFilepathForIndex(fileIndex) };
    }
    async postExcel(endpoint, body) {
        const response = await fetch(`${this.excelBackendUrl}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`${endpoint} failed (${response.status}): ${errorText}`);
        }
        return asRecord(await response.json());
    }
    async executeWithBackendAdapter(args) {
        const startedAt = Date.now();
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        const rawRequest = unwrapRequest(args);
        let request = normalizeReadRequest(rawRequest);
        const requestRecord = asRecord(rawRequest);
        const explicitReadCharts = Array.isArray(requestRecord.readCharts)
            ? requestRecord.readCharts.map((item) => asRecord(item))
            : [];
        const warnings = [];
        const errors = [];
        const data = {};
        const imageIds = [];
        const excelReadInfos = [];
        let operationCount = 0;
        if (!this.hasAnyOperation(request)) {
            request = {
                ...request,
                listSheets: [{}],
                readContent: [{ sheetName: "", mode: "structure" }],
            };
            warnings.push("No read operation was specified. Applied defaults: listSheets + readContent(mode='structure') on active sheet.");
        }
        if (request.readContent?.length) {
            operationCount += request.readContent.length;
            const items = request.readContent;
            const rawRows = [];
            for (const item of items) {
                try {
                    if (isDetailedReadMode(item.mode) && !item.targetRange) {
                        throw new Error("detailedValues/detailedValuesAndFormulas require targetRange.");
                    }
                    const fileIndex = parseFileIndex(item.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/read/content", {
                        ...apiBase,
                        sheet_name: item.sheetName,
                        target_range: item.targetRange || null,
                        mode: CONTENT_MODE_TO_BACKEND[item.mode],
                    });
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "read/content returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "readContent", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`readContent: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            const rows = normalizeReadBucketRows("readContent", rawRows);
            data.readContent = rows;
            for (let i = 0; i < rows.length; i += 1) {
                const requestItem = items[Math.min(i, items.length - 1)];
                const scopedImageIds = storeReadImages(rows[i], `content_${requestItem?.sheetName || "unknown"}_${requestItem?.targetRange || "FULL_SHEET"}`);
                imageIds.push(...scopedImageIds);
                excelReadInfos.push(buildContentReadInfo(requestItem, rows[i], scopedImageIds));
            }
        }
        if (request.readFormatting?.length) {
            operationCount += request.readFormatting.length;
            const items = request.readFormatting;
            const rawRows = [];
            for (const item of items) {
                try {
                    const fileIndex = parseFileIndex(item.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/read/formatting", {
                        ...apiBase,
                        sheet_name: item.sheetName,
                        target_range: item.targetRange,
                        include_content: item.includeContent ?? true,
                    });
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "read/formatting returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "readFormatting", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`readFormatting: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            const rows = normalizeReadBucketRows("readFormatting", rawRows);
            data.readFormatting = rows;
            for (let i = 0; i < rows.length; i += 1) {
                const requestItem = items[Math.min(i, items.length - 1)];
                const scopedImageIds = storeReadImages(rows[i], `formatting_${requestItem?.sheetName || "unknown"}_${requestItem?.targetRange || "FULL_SHEET"}`);
                imageIds.push(...scopedImageIds);
                excelReadInfos.push(buildFormattingReadInfo(requestItem, rows[i], scopedImageIds));
            }
        }
        const listChartsItems = Array.isArray(requestRecord.listCharts)
            ? requestRecord.listCharts.map((item) => asRecord(item))
            : [];
        if (listChartsItems.length > 0) {
            operationCount += listChartsItems.length;
            const rawRows = [];
            for (const item of listChartsItems) {
                try {
                    const fileIndex = parseFileIndex(item.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/list-charts", {
                        ...apiBase,
                        sheet_name: getString(item.sheetName) || "",
                    });
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "list-charts returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "listCharts", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`listCharts: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            data.listCharts = normalizeReadBucketRows("listCharts", rawRows);
            const rows = data.listCharts;
            for (let i = 0; i < rows.length; i += 1) {
                const requestItem = listChartsItems[Math.min(i, listChartsItems.length - 1)];
                const scopedImageIds = storeReadImages(rows[i], `list_charts_${getString(requestItem.sheetName) || "unknown"}`);
                imageIds.push(...scopedImageIds);
            }
        }
        if (explicitReadCharts.length > 0) {
            operationCount += explicitReadCharts.length;
            const items = explicitReadCharts.map((item) => {
                const dataMode = getString(item.dataMode);
                return {
                    sheetName: getString(item.sheetName) ||
                        getString(item.sheet_name) ||
                        "",
                    chartId: getString(item.chartId) || getString(item.chart_id),
                    chartName: getString(item.chartName) || getString(item.chart_name),
                    dataMode: dataMode === "summary-style" || dataMode === "point-level" || dataMode === "auto"
                        ? dataMode
                        : "auto",
                    fileIndex: parseFileIndex(item.fileIndex),
                };
            });
            const rawRows = [];
            for (const item of items) {
                try {
                    const fileIndex = parseFileIndex(item.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const dataMode = item.dataMode || "auto";
                    const chartLocator = item.chartId || item.chartName;
                    if (chartLocator) {
                        const chartPayload = await this.postExcel("/excel/read-chart", {
                            ...apiBase,
                            sheet_name: item.sheetName,
                            chart_id: item.chartId,
                            chart_name: item.chartName,
                            data_mode: dataMode,
                        });
                        if (chartPayload.success === false) {
                            throw new Error(getString(chartPayload.error) || "read-chart returned success=false.");
                        }
                        this.collectBackendWarnings(warnings, "readCharts", chartPayload);
                        rawRows.push(this.attachFileIndex(chartPayload, fileIndex));
                        continue;
                    }
                    const discovery = await this.postExcel("/excel/list-charts", {
                        ...apiBase,
                        sheet_name: item.sheetName,
                    });
                    if (discovery.success === false) {
                        throw new Error(getString(discovery.error) || "list-charts returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "readCharts", discovery);
                    const chartHandles = Array.isArray(discovery.data)
                        ? discovery.data.map((entry) => asRecord(entry))
                        : [];
                    for (const handle of chartHandles) {
                        const chartPayload = await this.postExcel("/excel/read-chart", {
                            ...apiBase,
                            sheet_name: item.sheetName,
                            chart_id: getString(handle.chartId) || undefined,
                            chart_name: getString(handle.chartName) || undefined,
                            data_mode: dataMode,
                        });
                        if (chartPayload.success === false) {
                            errors.push(`readCharts: read-chart failed for ${item.sheetName}/${getString(handle.chartId) || getString(handle.chartName) || "unknown"}`);
                            continue;
                        }
                        this.collectBackendWarnings(warnings, "readCharts", chartPayload);
                        rawRows.push(this.attachFileIndex(chartPayload, fileIndex));
                    }
                }
                catch (error) {
                    errors.push(`readCharts: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            const rows = normalizeReadBucketRows("readCharts", rawRows);
            data.readCharts = rows;
            for (let i = 0; i < rows.length; i += 1) {
                const requestItem = pickRequestItemForChartResult(items, asRecord(rows[i]), i);
                const scopedImageIds = storeReadImages(rows[i], `chart_${requestItem?.sheetName || "unknown"}_${requestItem?.chartId || requestItem?.chartName || "unknown"}`);
                imageIds.push(...scopedImageIds);
                excelReadInfos.push(buildChartReadInfo(requestItem, rows[i], scopedImageIds));
            }
        }
        if (request.formulaTrace?.length) {
            operationCount += request.formulaTrace.length;
            const rawRows = [];
            for (const item of request.formulaTrace) {
                try {
                    const itemRecord = asRecord(item);
                    const cellAddress = getString(item.cellAddress) || "";
                    if (cellAddress.includes(":")) {
                        throw new Error(`requires a single cell, not range '${cellAddress}'.`);
                    }
                    const fileIndex = parseFileIndex(itemRecord.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/read/formula-trace", {
                        ...apiBase,
                        sheet_name: item.sheetName || "",
                        cell_address: cellAddress,
                        direction: item.direction || "both",
                        recursive: typeof item.recursive === "boolean" ? item.recursive : true,
                        max_depth: typeof item.maxDepth === "number" ? item.maxDepth : 10,
                    });
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "read/formula-trace returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "formulaTrace", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`formulaTrace: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            data.formulaTrace = normalizeReadBucketRows("formulaTrace", rawRows);
        }
        if (request.tableAnalysis?.length) {
            operationCount += request.tableAnalysis.length;
            const rawRows = [];
            for (const item of request.tableAnalysis) {
                try {
                    const itemRecord = asRecord(item);
                    const fileIndex = parseFileIndex(itemRecord.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/read/table-analysis", {
                        ...apiBase,
                        sheet_name: item.sheetName || "",
                        target_range: item.targetRange || "",
                        group_by: item.groupBy || "column",
                        value_threshold: typeof item.valueThreshold === "number" ? item.valueThreshold : 0.05,
                        include_empty_analysis: typeof item.includeEmptyAnalysis === "boolean" ? item.includeEmptyAnalysis : true,
                    });
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "read/table-analysis returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "tableAnalysis", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`tableAnalysis: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            data.tableAnalysis = normalizeReadBucketRows("tableAnalysis", rawRows);
        }
        if (request.listSheets?.length) {
            operationCount += request.listSheets.length;
            const rawRows = [];
            for (const item of request.listSheets) {
                try {
                    const fileIndex = parseFileIndex(item.fileIndex);
                    const apiBase = await this.getApiBaseForItem(fileIndex, sessionId);
                    const payload = await this.postExcel("/excel/read/sheets", apiBase);
                    if (payload.success === false) {
                        throw new Error(getString(payload.error) || "read/sheets returned success=false.");
                    }
                    this.collectBackendWarnings(warnings, "listSheets", payload);
                    rawRows.push(this.attachFileIndex(payload, fileIndex));
                }
                catch (error) {
                    errors.push(`listSheets: ${error instanceof Error ? error.message : String(error)}`);
                }
            }
            const rows = normalizeReadBucketRows("listSheets", rawRows);
            data.listSheets = rows;
            for (let i = 0; i < rows.length; i += 1) {
                const scopedImageIds = storeReadImages(rows[i], "list_sheets");
                imageIds.push(...scopedImageIds);
            }
        }
        return this.finalizeResult({
            startedAt,
            operationCount,
            data,
            warnings,
            errors,
            imageIds,
            excelReadInfos,
        });
    }
}
